/**
 * Linear Assets — Vegetation Clearance & Response Planning (Step 8)
 *
 * Operator can override AI-proposed crew assignment, equipment, intervention
 * method, and re-scan interval before the work order is dispatched.
 */

import React, { useState } from 'react';
import { Scissors, ShieldCheck, Users, AlertTriangle, CheckCircle2, Edit3, RotateCcw } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';

// ─── AI-proposed defaults ────────────────────────────────────────────────────

interface WorkOrderParams {
  crew:          string;
  equipment:     string;
  method:        string;
  rescanDays:    number;
  operatorNotes: string;
}

const AI_DEFAULTS: WorkOrderParams = {
  crew:          'Alpha Arborist Team (4 crew)',
  equipment:     'Insulated Bucket Truck + Chipper',
  method:        'Mechanical Canopy Trimming + Herbicide Stump Treatment',
  rescanDays:    21,
  operatorNotes: '',
};

const CREW_OPTIONS = [
  'Alpha Arborist Team (4 crew)',
  'Bravo Ground Clearing Unit (6 crew)',
  'Charlie Vegetation Crew (3 crew)',
  'Delta Emergency Response Team (8 crew)',
  'External Contractor (TBD)',
];

const EQUIPMENT_OPTIONS = [
  'Insulated Bucket Truck + Chipper',
  'Chainsaw Ground Team Only',
  'Tracked Elevated Work Platform',
  'Helicopter-Based Trimming Crew',
  'Combination (Truck + Chainsaw)',
];

const METHOD_OPTIONS = [
  'Mechanical Canopy Trimming + Herbicide Stump Treatment',
  'Full Felling + Stump Grinding',
  'Selective Pruning Only',
  'Herbicide Foliar Application Only',
  'Emergency Cut-Back (Critical Clearance)',
];

const SELECT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export const LinearResponsePlanningPage: React.FC = () => {
  const [params, setParams]       = useState<WorkOrderParams>({ ...AI_DEFAULTS });
  const [dispatched, setDispatched] = useState(false);
  const [success, setSuccess]     = useState<string | null>(null);

  const setP = <K extends keyof WorkOrderParams>(k: K, v: WorkOrderParams[K]) =>
    setParams((p) => ({ ...p, [k]: v }));

  const overriddenFields = (Object.keys(params) as (keyof WorkOrderParams)[]).filter(
    (k) => k !== 'operatorNotes' && params[k] !== AI_DEFAULTS[k]
  );
  const hasOverrides = overriddenFields.length > 0;

  const resetToDefaults = () => setParams({ ...AI_DEFAULTS, operatorNotes: params.operatorNotes });

  const handleDispatch = () => {
    setDispatched(true);
    setSuccess(
      `Clearance work order dispatched to ${params.crew}. Re-survey scheduled +${params.rescanDays} days.` +
      (hasOverrides ? ` (${overriddenFields.length} operator override${overriddenFields.length > 1 ? 's' : ''} applied)` : '')
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Scissors className="w-7 h-7 text-amber-500" /> Vegetation Clearance &amp; Response Planning (Step 8)
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Review AI-proposed crew, equipment, and schedule — override as needed before dispatching the work order
          </p>
        </div>
      </div>

      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Target Zone (read-only context) ── */}
        <Card className="space-y-4">
          <h3 className="font-bold text-sm text-gray-900 dark:text-white uppercase tracking-wider">Confirmed Target Zone</h3>
          <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg text-xs space-y-1.5">
            <p className="font-bold text-amber-950 dark:text-amber-200">Power Line Sector 7 &bull; Tower 42 to 44</p>
            <p className="text-gray-600 dark:text-gray-400">GPS: <strong>-37.8136, 144.9631</strong></p>
            <p className="text-gray-600 dark:text-gray-400">Clearance Span: <strong>140 metres</strong></p>
            <p className="text-gray-600 dark:text-gray-400">AI Diagnosis: <strong>Radial clearance breach 0.8 m — Class A vegetation encroachment</strong></p>
          </div>

          {/* Safety checklist */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-green-500" /> Pre-Dispatch Checklist
            </p>
            <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
              {[
                'Notify transmission operator ≥ 24 h prior',
                'Confirm crew holds HV line proximity certification',
                'Issue exclusion zone notices to adjacent landowners',
                'Verify equipment is insulated to ≥ 33kV rating',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />{item}
                </li>
              ))}
              {hasOverrides && (
                <li className="flex items-start gap-2 pt-1 border-t border-gray-100 dark:border-gray-800 mt-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-amber-700 dark:text-amber-400">Overrides applied</strong> — log justification per QA protocol.</span>
                </li>
              )}
            </ul>
          </div>
        </Card>

        {/* ── Work Order Parameters (editable) ── */}
        <Card className="lg:col-span-2 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-sm text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-amber-500" /> Work Order Parameters
              {hasOverrides && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-semibold">
                  {overriddenFields.length} override{overriddenFields.length > 1 ? 's' : ''}
                </span>
              )}
            </h3>
            {hasOverrides && (
              <button type="button" onClick={resetToDefaults}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <RotateCcw className="w-3 h-3" /> Reset to AI defaults
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Crew */}
            <div className={`p-3 rounded-lg border ${params.crew !== AI_DEFAULTS.crew ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700'}`}>
              <label className={`block text-[11px] font-semibold uppercase tracking-wider mb-1.5 ${params.crew !== AI_DEFAULTS.crew ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                Assigned Crew {params.crew !== AI_DEFAULTS.crew && '· overridden'}
                <span className="block text-[10px] font-normal normal-case tracking-normal text-gray-400 mt-0.5">AI: {AI_DEFAULTS.crew}</span>
              </label>
              <select value={params.crew} onChange={(e) => setP('crew', e.target.value)} className={SELECT_CLS}>
                {CREW_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* Equipment */}
            <div className={`p-3 rounded-lg border ${params.equipment !== AI_DEFAULTS.equipment ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700'}`}>
              <label className={`block text-[11px] font-semibold uppercase tracking-wider mb-1.5 ${params.equipment !== AI_DEFAULTS.equipment ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                Required Equipment {params.equipment !== AI_DEFAULTS.equipment && '· overridden'}
                <span className="block text-[10px] font-normal normal-case tracking-normal text-gray-400 mt-0.5">AI: {AI_DEFAULTS.equipment}</span>
              </label>
              <select value={params.equipment} onChange={(e) => setP('equipment', e.target.value)} className={SELECT_CLS}>
                {EQUIPMENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* Method */}
            <div className={`sm:col-span-2 p-3 rounded-lg border ${params.method !== AI_DEFAULTS.method ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700'}`}>
              <label className={`block text-[11px] font-semibold uppercase tracking-wider mb-1.5 ${params.method !== AI_DEFAULTS.method ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                Intervention Method {params.method !== AI_DEFAULTS.method && '· overridden'}
                <span className="block text-[10px] font-normal normal-case tracking-normal text-gray-400 mt-0.5">AI: {AI_DEFAULTS.method}</span>
              </label>
              <select value={params.method} onChange={(e) => setP('method', e.target.value)} className={SELECT_CLS}>
                {METHOD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* Re-scan interval */}
          <div className={`p-3 rounded-lg border ${params.rescanDays !== AI_DEFAULTS.rescanDays ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700'}`}>
            <label className={`block text-[11px] font-semibold uppercase tracking-wider mb-2 ${params.rescanDays !== AI_DEFAULTS.rescanDays ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
              Efficacy Re-Scan Interval {params.rescanDays !== AI_DEFAULTS.rescanDays && '· overridden'}
              <span className="block text-[10px] font-normal normal-case tracking-normal text-gray-400 mt-0.5">AI default: +{AI_DEFAULTS.rescanDays} days</span>
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              {[7, 14, 21, 28, 42].map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setP('rescanDays', days)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${params.rescanDays === days ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-amber-300'}`}
                >
                  +{days} Days
                </button>
              ))}
            </div>
          </div>

          {/* Operator notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Operator Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={2}
              className={`${SELECT_CLS} resize-none`}
              placeholder="Site access instructions, coordination contacts, override justification…"
              value={params.operatorNotes}
              onChange={(e) => setP('operatorNotes', e.target.value)}
            />
          </div>

          <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
            <Button variant="primary" onClick={handleDispatch} disabled={dispatched} className="w-full">
              {dispatched
                ? 'Work Order Dispatched'
                : `Dispatch Clearance Work Order & Schedule Re-Scan${hasOverrides ? ' (with overrides)' : ''}`}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LinearResponsePlanningPage;
